import { Component, computed, inject, input } from '@angular/core';
import { RidScrapbookService } from '../../services/rid-scrapbook.service';

// a mask has to cover everything a stroke can reach, in the svg's own (content box pixel) units
const MASK_EXTENT = 100_000;

let instanceCount = 0;

interface Stroke {
  zIndex: number;
  color: string | null; // null erases
  width: number;
  path: string;
}

/** Something on the page that isn't drawn, like a sticker: strokes above it can't share an svg with strokes below it. */
const STICKER = 'sticker';

interface PencilRun {
  key: string;
  pencils: Stroke[];
  erasers: Stroke[]; // every eraser stroke drawn after these pencils
}

/** Pencil strokes with no sticker between them, rendered as one svg at the z-index of the first one. */
interface DrawingGroup {
  key: string;
  zIndex: number;
  runs: PencilRun[];
}

/** A curve through the midpoints between the recorded points (flattened [x0, y0, x1, y1, ...]), which smooths out the corners of the polyline. */
function toSvgPath(points: number[]): string {
  const p = points;
  if (p.length < 4) return `M${p[0]} ${p[1]}l0 0`; // a single tap draws a dot (thanks to the round caps)

  let d = `M${p[0]} ${p[1]}`;
  for (let i = 2; i < p.length - 2; i += 2) {
    d += `Q${p[i]} ${p[i + 1]} ${(p[i] + p[i + 2]) / 2} ${(p[i + 1] + p[i + 3]) / 2}`;
  }
  return `${d}L${p[p.length - 2]} ${p[p.length - 1]}`;
}

/**
 * The strokes of one week. A sticker can't sit between two paths of the same svg, so the pencil strokes between two stickers get an svg of their own,
 * each at its own z-index; the host is `display: contents`, so these svgs stack between the stickers of the page.
 * The eraser only rubs out what was drawn before it (in any svg), never the stickers, so every run of pencils is masked by the eraser strokes after it.
 * Several copies of the same week can be on screen at once (e.g. both halves of a spread), so the stroke being drawn lives in the service.
 */
@Component({
  selector: 'rid-scrapbook-drawing',
  template: `
    @for (group of groups(); track group.key) {
      <svg
        class="pointer-events-none absolute inset-0 size-full overflow-visible"
        [style.z-index]="group.zIndex"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <defs>
          @for (run of group.runs; track run.key) {
            @if (run.erasers.length) {
              <mask
                [attr.id]="maskId(run)"
                maskUnits="userSpaceOnUse"
                [attr.x]="-extent"
                [attr.y]="-extent"
                [attr.width]="extent * 2"
                [attr.height]="extent * 2"
              >
                <rect [attr.x]="-extent" [attr.y]="-extent" [attr.width]="extent * 2" [attr.height]="extent * 2" fill="white" />
                @for (eraser of run.erasers; track $index) {
                  <path [attr.d]="eraser.path" stroke="black" [attr.stroke-width]="eraser.width" />
                }
              </mask>
            }
          }
        </defs>

        @for (run of group.runs; track run.key) {
          <g [attr.mask]="run.erasers.length ? 'url(#' + maskId(run) + ')' : null">
            @for (pencil of run.pencils; track $index) {
              <path [attr.d]="pencil.path" [attr.stroke]="pencil.color" [attr.stroke-width]="pencil.width" />
            }
          </g>
        }
      </svg>
    }
  `,
  host: { class: 'contents' },
})
export class RidScrapbookDrawing {
  private readonly scrapbook = inject(RidScrapbookService);

  week = input.required<number>();

  protected readonly extent = MASK_EXTENT;
  private readonly instanceId = ++instanceCount;

  // kept apart from the active stroke so the saved strokes are not smoothed again on every pointer move
  private readonly savedContent = computed<(Stroke | typeof STICKER)[]>(() =>
    this.scrapbook
      .itemsFor(this.week())
      .map(({ zIndex, content: c }) =>
        c.type === 'DRAWING' ? { zIndex, color: c.color, width: c.width, path: toSvgPath(c.points) } : STICKER,
      ),
  );

  private readonly content = computed<(Stroke | typeof STICKER)[]>(() => {
    const content = this.savedContent();
    const active = this.scrapbook.activeStroke();
    if (active?.week !== this.week()) return content;

    // the stroke being drawn ends up on top of everything once it is added
    return [...content, { zIndex: this.scrapbook.nextZIndex(), color: active.color, width: active.width, path: toSvgPath(active.points) }];
  });

  protected readonly groups = computed<DrawingGroup[]>(() => {
    const content = this.content();
    const groups: DrawingGroup[] = [];
    let group: DrawingGroup | undefined;
    let run: PencilRun | undefined;
    content.forEach((entry, i) => {
      if (entry === STICKER) {
        group = run = undefined;
        return;
      }
      if (entry.color === null) {
        run = undefined;
        return;
      }

      if (!group) {
        group = { key: `${i}`, zIndex: entry.zIndex, runs: [] };
        groups.push(group);
      }
      if (!run) {
        run = { key: `${i}`, pencils: [], erasers: content.slice(i + 1).filter((e): e is Stroke => e !== STICKER && e.color === null) };
        group.runs.push(run);
      }
      run.pencils.push(entry);
    });
    return groups;
  });

  protected maskId(run: PencilRun): string {
    return `rid-scrapbook-drawing-${this.instanceId}-${run.key}`;
  }
}
